import React, { useState, useEffect } from 'react'
import V2Layout from '../components/V2Layout'
import SEO from '../components/SEO'
import tahoeSkiing from '../images/Life/Tahoe_Skiing.jpg'
import napaValley from '../images/Life/Napa_Valley.jpg'
import riverRafting from '../images/Life/River_Rafting.jpg'
import midtownSac from '../images/Life/Midtown_Sac.jpg'

const VALUES = [
    { num: '01', title: 'Pitch-In, Roll-Up', desc: 'We pitch in and help each other. We roll up our sleeves and do what it takes.' },
    { num: '02', title: 'High Performance', desc: 'We set high standards and take pride in reaching them. Quality, safety, and efficiency permeate everything we do.' },
    { num: '03', title: 'Stay Flexible', desc: 'We make informed bets on hard problems. We plan, execute, and replan when needed.' },
    { num: '04', title: 'Trust Is Our Boost', desc: 'We speak up and share ideas with an open mind. We trust the best intentions in every action.' },
    { num: '05', title: 'We Liftoff Together', desc: 'We succeed when the team succeeds. We gladly put aside self-interest and keep egos at the door.' }
]

const BENEFITS = [
    'Competitive salary aligned to industry standards',
    '3/10, 4/10, 5/8, 9/80 schedule options',
    'Health, dental, and vision coverage',
    '401(k) with 6% company match',
    'Paid vacation and sick time',
    'Company holidays',
    'Career development and skills training',
    'Small team, direct access to leadership',
]

// const CULTURE = [
//     { num: '01', title: 'People First', desc: 'Our people are our greatest asset. We prioritize well-being, growth, and a workplace where everyone feels valued.' },
//     { num: '02', title: 'Excellence Focused', desc: 'We hold ourselves to the highest standards in everything we do, from the parts we machine to the way we treat each other.' },
//     { num: '03', title: 'Generous', desc: 'We give freely of our time, knowledge, and support. Generosity fuels collaboration and builds lasting trust.' },
//     { num: '04', title: 'Resilient', desc: 'Setbacks do not define us. How we respond does. We adapt, persevere, and come back stronger every time.' },
//     { num: '05', title: 'Highly Engaged', desc: 'We show up with energy and purpose. Engaged teams build better products and create better outcomes for everyone.' },
// ]

// TODO: Replace placeholder roles, bios, and initials with real team members before go-live.
const TEAM = [
    { role: 'Director of Manufacturing', bio: '15+ years in aerospace manufacturing. Oversees all shop floor operations and ensures every part meets flight-ready standards.', initials: 'DM' },
    { role: 'Engineering Manager', bio: 'Leads the engineering team with deep expertise in CNC process optimization and DFM for complex aerospace geometries.', initials: 'EM' },
    { role: 'Quality Assurance Manager', bio: 'AS9100 lead auditor with a passion for building quality systems that empower machinists to do their best work.', initials: 'QM' },
    { role: 'Operations Manager', bio: 'Keeps production moving, from scheduling and logistics to continuous improvement initiatives across the facility.', initials: 'OM' },
    { role: 'HR & Culture Manager', bio: 'Drives recruiting, onboarding, and the culture programs that make PFC a great place to work.', initials: 'HR' },
]

const NEARBY_ACTIVITIES = [
    {
        title: 'Lake Tahoe Skiing',
        body: 'World-class slopes are about 90 minutes from our Lincoln facility. Spend weekends on Tahoe\'s legendary runs - from groomed cruisers to backcountry bowls - with resorts open from late fall through spring.',
        image: tahoeSkiing,
        alt: 'Skier on a Lake Tahoe slope with mountain views',
    },
    {
        title: 'Napa Valley',
        body: 'Wine country is about 1.5 hours from Lincoln - rolling vineyards, tasting rooms and farm-to-table dining for a relaxed weekend away.',
        image: napaValley,
        alt: 'Vineyards in Napa Valley',
    },
    {
        title: 'American River Rafting',
        body: 'Class II–IV whitewater on the American River is a local favorite for spring and summer adventure. Guided trips and easy day access make it a go-to outing for teams and families across Placer County.',
        image: riverRafting,
        alt: 'Rafting on the American River',
    },
    {
        title: 'Midtown Sacramento',
        body: 'Farm-to-fork dining, live music, and a walkable arts district are roughly 30 minutes away in Midtown Sacramento. It\'s an easy evening out after a day on the shop floor.',
        image: midtownSac,
        alt: 'Midtown Sacramento street scene',
    },
]

const formatDate = iso => new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

/** Strip ADP boilerplate and normalize scraped job description HTML */
function cleanJobHtml(html) {
    if (!html) return ''

    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
    const root = doc.body.firstChild

    // Remove noise elements entirely
    root.querySelectorAll('script, style, link, meta, button, input, select, textarea, nav, footer, header, iframe').forEach(el => el.remove())

    // Remove ADP boilerplate containers by class hint
    root.querySelectorAll('[class*="social"], [class*="share"], [class*="back-btn"], [class*="apply-btn"], [class*="navigation"], [class*="breadcrumb"]').forEach(el => el.remove())

    // Strip presentational attributes from every element
    root.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'))
    root.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'))
    root.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'))

    // Unwrap <font> tags — purely presentational
    root.querySelectorAll('font').forEach(el => el.replaceWith(...el.childNodes))

    // Unwrap <span> tags — ADP uses them only for inline styling
    root.querySelectorAll('span').forEach(el => el.replaceWith(...el.childNodes))

    // Convert leaf <div> blocks (no block-level children) to <p> for consistency
    const BLOCK_TAGS = new Set(['DIV', 'P', 'UL', 'OL', 'TABLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])
    root.querySelectorAll('div').forEach(el => {
        const hasBlockChild = [...el.children].some(c => BLOCK_TAGS.has(c.tagName))
        if (!hasBlockChild && el.textContent.trim()) {
            const p = doc.createElement('p')
            p.append(...el.childNodes)
            el.replaceWith(p)
        }
    })

    // Remove empty block elements (3 passes handles nesting)
    for (let i = 0; i < 3; i++) {
        root.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach(el => {
            if (!el.textContent.trim()) el.remove()
        })
    }

    let result = root.innerHTML

    // Remove known ADP boilerplate text patterns
    result = result.replace(/Facebook\s*LinkedIn\s*Email\s*X\s*Copy\s*URL/gi, '')
    result = result.replace(/If you are unable to complete this application due to a disability[^<]*/gi, '')
    result = result.replace(/Equal Opportunity Employer[^<]*/gi, '')

    // Collapse consecutive <br> tags to a single one
    result = result.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>')
    // Strip leading/trailing <br>
    result = result.replace(/^(\s*<br\s*\/?>\s*)+/i, '').replace(/(\s*<br\s*\/?>\s*)+$/i, '')

    return result.trim()
}

const PILLAR_H3 = {
    fontSize: '1.0625rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    marginBottom: '0.75rem',
    letterSpacing: '-0.01em',
}

export default function CareersScreen() {
    const [jobs, setJobs] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [filterDept, setFilterDept] = useState('All')
    const [expandedId, setExpandedId] = useState(null)

    useEffect(() => {
        setLoading(true)
        fetch('/api/jobs')
            .then(res => {
                if (!res.ok) throw new Error('Failed to load jobs')
                return res.json()
            })
            .then(data => {
                setJobs(data)
                setError(null)
            })
            .catch(() => {
                setError('Listings are temporarily unavailable')
            })
            .finally(() => {
                setLoading(false)
            })
    }, [])

    const depts = ['All', ...Array.from(new Set(jobs.map(j => j.department)))]
    const filtered = filterDept === 'All' ? jobs : jobs.filter(j => j.department === filterDept)

    return (
        <V2Layout pageClass="pfc-careers">
            <SEO
                title="Careers"
                description="Precision Fluid Controls manufactures valves and pressure controls for aerospace, launch vehicle, medical, industrial, maritime and defense applications. We are growing and looking for machinists, engineers, and quality professionals in Lincoln, CA."
            />

            {/* ── Hero ── */}
            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">Careers at PFC</p>
                    <h1 className="pfc-page-hero-title">Build the parts<br />that reach orbit.</h1>
                    <p className="pfc-page-hero-sub">
                        Precision Fluid Controls manufactures valves and pressure controls for aerospace, launch vehicle, medical, industrial, maritime and defense applications. We are growing and looking for machinists, engineers, and quality professionals in Lincoln, CA.
                    </p>
                    <div style={{ marginTop: '2rem' }}>
                        <a
                            href="#positions"
                            className="v2-btn-primary"
                        >
                            View Open Positions
                        </a>
                    </div>
                </div>
            </section>

            {/* ── Values ── */}
            <section className="v2-content  visually-hidden" >
                <div className="v2-approach-inner">
                    <div className="v2-approach-header">
                        <p className="v2-section-label">Culture &amp; Values</p>
                        <h2 className="v2-approach-title">What we believe</h2>
                    </div>
                    <div className="v2-pillars">
                        {VALUES.map(v => (
                            <div className="v2-pillar" key={v.num}>
                                <span className="v2-pillar-num">{v.num}</span>
                                <h3 style={PILLAR_H3}>{v.title}</h3>
                                <p>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Benefits ── */}
            <section className="v2-approach">
                <div className="v2-approach-inner">
                    <div className="v2-approach-header">
                        <p className="v2-section-label">Why Join PFC</p>
                        <h2 className="v2-approach-title">What we offer</h2>
                    </div>
                    <ul
                        className="v2-spec-list"
                        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', columnGap: '3rem', margin: 0 }}
                    >
                        {BENEFITS.map((b, i) => (
                            <li key={i}>{b}</li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Culture ── */}
            {/* <section className="v2-content">
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div className="v2-approach-header">
                        <p className="v2-section-label">Who We Are</p>
                        <h2 className="v2-approach-title">Our culture</h2>
                    </div>
                    <div
                        className="v2-pillars"
                        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
                    >
                        {CULTURE.map(c => (
                            <div className="v2-pillar" key={c.num}>
                                <span className="v2-pillar-num">{c.num}</span>
                                <h3 style={PILLAR_H3}>{c.title}</h3>
                                <p>{c.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ── Life near Lincoln ── */}
            <section className="v2-content" style={{ paddingBottom: '3rem' }}>
                <div className="v2-approach-inner">
                    <div className="v2-approach-header" style={{ marginBottom: 0 }}>
                        <p className="v2-section-label">Life Beyond the Shop</p>
                        <h2 className="v2-approach-title">Work in Lincoln, live in Northern California</h2>
                        <p className="v2-content-body" style={{ maxWidth: 640, marginTop: '1.25rem' }}>
                            Our facility sits in Lincoln, CA ~ Close to Sacramento and a short drive from Tahoe,
                            the American River and everything the region offers outside of work.
                        </p>
                    </div>
                </div>
            </section>

            {NEARBY_ACTIVITIES.map((spot, i) => (
                <section
                    key={spot.title}
                    className={`pfc-life-section ${i % 2 === 1 ? 'v2-approach' : 'v2-content'}`}
                >
                    <div className={`v2-content-inner${i % 2 === 1 ? ' v2-reverse' : ''}`}>
                        <div className="v2-content-col">
                            {/* <p className="v2-section-label">{String(i + 1).padStart(2, '0')}</p> */}
                            <h3 className="v2-content-title">{spot.title}</h3>
                            <p className="v2-content-body">{spot.body}</p>
                        </div>
                        <div className="v2-content-col">
                            <div className="v2-img-wrap">
                                <img src={spot.image} className="v2-img" alt={spot.alt} />
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* ── Open Positions ── */}
            <section id="positions" className="v2-approach">
                <div className="v2-approach-inner">
                    <div className="v2-approach-header">
                        <p className="v2-section-label">Open Positions</p>
                        <h2 className="v2-approach-title">Join the team</h2>
                    </div>

                    {loading ? (
                        <div style={{ padding: '3rem 0', textAlign: 'center' }}>
                            <span style={{
                                display: 'inline-block',
                                width: 32,
                                height: 32,
                                border: '3px solid var(--v2-border)',
                                borderTopColor: 'var(--v2-primary)',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite',
                            }} />
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            <p style={{ color: 'var(--v2-text2)', marginTop: '1rem', fontSize: '0.9375rem' }}>
                                Loading positions…
                            </p>
                        </div>
                    ) : error ? (
                        <p style={{ color: 'var(--v2-text2)', padding: '3rem 0', fontSize: '0.9375rem' }}>
                            {error}
                        </p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '2.5rem' }}>
                                {depts.map(d => (
                                    <button
                                        key={d}
                                        onClick={() => setFilterDept(d)}
                                        style={{
                                            padding: '0.4rem 1.125rem',
                                            borderRadius: 4,
                                            border: `1px solid ${filterDept === d ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                                            background: filterDept === d ? 'var(--v2-primary)' : 'var(--v2-surface)',
                                            color: filterDept === d ? 'var(--v2-bg)' : 'var(--v2-text)',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontSize: '0.75rem',
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            fontFamily: 'inherit',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>

                            {filtered.length === 0 ? (
                                <p style={{ color: 'var(--v2-text2)', padding: '3rem 0', fontSize: '0.9375rem' }}>
                                    {jobs.length === 0
                                        ? 'No positions are currently open.'
                                        : 'No open positions in this department right now.'}
                                </p>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1px',
                                    background: 'var(--v2-border)',
                                    border: '1px solid var(--v2-border)',
                                }}>
                                    {filtered.map(job => {
                                        const open = expandedId === job.id
                                        return (
                                            <div key={job.id} style={{ background: 'var(--v2-surface)' }}>
                                                <button
                                                    onClick={() => setExpandedId(open ? null : job.id)}
                                                    aria-expanded={open}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        padding: '1.5rem 1.75rem',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        fontFamily: 'inherit',
                                                    }}
                                                >
                                                    <div>
                                                        <h3 style={{
                                                            fontSize: '1.0625rem',
                                                            fontWeight: 700,
                                                            color: 'var(--v2-text)',
                                                            marginBottom: '0.5rem',
                                                            letterSpacing: '-0.01em',
                                                        }}>
                                                            {job.title}
                                                        </h3>
                                                        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                                                            {[job.location, job.department, job.type].filter(Boolean).map((tag, i) => (
                                                                <span key={i} style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-text2)' }}>
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <span style={{
                                                        color: 'var(--v2-primary)',
                                                        fontSize: '0.6875rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.07em',
                                                        textTransform: 'uppercase',
                                                        marginLeft: '1rem',
                                                        whiteSpace: 'nowrap',
                                                        paddingTop: '0.25rem',
                                                    }}>
                                                        {open ? 'Collapse' : 'View'}
                                                    </span>
                                                </button>

                                                {open && (
                                                    <div style={{ padding: '0 1.75rem 1.75rem', borderTop: '1px solid var(--v2-border)' }}>
                                                        <div
                                                            className="pfc-job-description"
                                                            style={{
                                                                color: 'var(--v2-text2)',
                                                                lineHeight: 1.75,
                                                                margin: '1.25rem 0',
                                                                fontSize: '0.9375rem',
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: cleanJobHtml(job.description) }}
                                                        />
                                                        {job.requirements && (
                                                            <>
                                                                <p style={{
                                                                    fontSize: '0.6875rem',
                                                                    fontWeight: 700,
                                                                    letterSpacing: '0.1em',
                                                                    textTransform: 'uppercase',
                                                                    color: 'var(--v2-text)',
                                                                    marginBottom: '0.75rem',
                                                                }}>
                                                                    Requirements
                                                                </p>
                                                                <ul className="v2-spec-list" style={{ marginTop: 0, marginBottom: '1.75rem' }}>
                                                                    {job.requirements.split('\n').filter(r => r.trim()).map((r, i) => (
                                                                        <li key={i}>{r}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                                                            <a
                                                                href={job.adp_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="v2-btn-primary"
                                                                style={{
                                                                    padding: '0.75rem 1.75rem',
                                                                }}
                                                            >
                                                                Apply Now
                                                            </a>
                                                            {job.posted_date && (
                                                                <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text3)' }}>
                                                                    Posted {formatDate(job.posted_date)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

        </V2Layout>
    )
}

function TeamCard({ member }) {
    const [hovered, setHovered] = useState(false)
    return (
        <div
            style={{
                background: 'var(--v2-surface)',
                padding: '2rem 1.5rem',
                textAlign: 'center',
                transition: 'box-shadow 0.25s ease',
                ...(hovered ? { boxShadow: '0 8px 32px rgba(0,0,0,0.12)', position: 'relative', zIndex: 1 } : {}),
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'var(--v2-text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem',
            }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 800, color: 'var(--v2-bg)', letterSpacing: '0.03em' }}>
                    {member.initials}
                </span>
            </div>
            <h3 style={{
                fontSize: '0.9375rem',
                fontWeight: 700,
                color: 'var(--v2-text)',
                marginBottom: '0.375rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
            }}>
                {member.role}
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text2)', lineHeight: 1.65, margin: 0 }}>
                {member.bio}
            </p>
        </div>
    )
}
